/**
 * The secret room behind a red door (spec 02 §9.3) - integrator-owned. No monsters, no time limit:
 * 36 diamonds worth 12,000 each, collected by touch as other items are (16 px, D_29C0). Once 24 are
 * taken the floor's exit opens; dropping through it, or taking all 36, leaves the room, and play
 * resumes two levels on from the door (door on 20 -> level 22).
 */

import { SECRET_EXIT_COLS, secretDiamonds } from '../levels/secretRoom';
import { endRound } from './flow';
import { addScore } from './score';
import type { World } from './types';

/** What the room keeps (on the world as `secret`). */
export interface SecretState {
  /** The level whose red door led here. */
  from: number;
  taken: number;
  open: boolean;
}

/** 12,000 a diamond (stored units). */
const DIAMOND_UNITS = 1200;
/** Diamonds that open the floor's exit. */
const OPEN_AT = 24;
/** Every diamond taken: the room ends this many frames later. */
const ALL_TAKEN_FRAMES = 50;
/** The diamonds are drawn as the three gems of the food table, in turn (yellow, green, purple). */
const DIAMOND_FOODS: readonly number[] = [41, 43, 44];
/** A player this far below the floor's standing line (VIC Y $DD) is dropping out through the exit. */
const FLOOR_Y = 0xdd;
const OUT_BELOW = 6;
const PICKUP = 16;
const FLOOR_ROW = 24;
const COLS = 32;

/** Set the room up for a world made on `SECRET_ROOM`: the diamonds, and where to go after. */
export function setupSecret(world: World, from: number): void {
  world.secret = { from, taken: 0, open: false };
  secretDiamonds().forEach((t, i) => {
    world.items.push({
      id: world.nextId++, kind: 'diamond', item: DIAMOND_FOODS[i % DIAMOND_FOODS.length] ?? 41,
      x: t.x * 8 + 0x14, y: t.y * 8 + 0x2d, value: DIAMOND_UNITS, life: -1, falling: false,
    });
  });
}

function leave(world: World, frames: number): void {
  const s = world.secret;
  if (s) endRound(world, { kind: 'resume', to: Math.min(100, s.from + 2) }, frames);
}

/** One I-tick in the room: pickups, the exit opening, a player leaving through it. */
export function secretTick(world: World): void {
  const s = world.secret;
  if (!s) return;
  for (const p of world.players) {
    if (p.state !== 'play') continue;
    for (let i = world.items.length - 1; i >= 0; i--) {
      const it = world.items[i];
      if (!it || it.kind !== 'diamond') continue;
      if (Math.abs(p.x - it.x) >= PICKUP || Math.abs(p.y - it.y) >= PICKUP) continue;
      world.items.splice(i, 1);
      addScore(world, p.id, it.value);
      s.taken++;
      world.events.push({ type: 'collect', player: p.id, itemId: it.id, value: it.value });
      if (s.taken === secretDiamonds().length) leave(world, ALL_TAKEN_FRAMES);
    }
    if (s.open && p.y > FLOOR_Y + OUT_BELOW && p.y < 0xf5) leave(world, 1);
  }
  if (!s.open && s.taken >= OPEN_AT) {
    s.open = true;
    const solid = world.playfield.solid as number[];
    for (const c of SECRET_EXIT_COLS) solid[FLOOR_ROW * COLS + c] = 0;
  }
}
